// Per-user API keys with per-key permissions (see design.md → API Keys).
//
// A signed-in user mints opaque API keys that authenticate PROGRAMMATIC calls
// to the SAME AppSync API as their owning user, subject to the key's
// permissions. Permissions are TWO independent dimensions:
//   1. operation scopes — a subset of a fixed vocabulary (e.g. `projects:read`,
//      `courses:write`, `generation:run`). Every gated GraphQL field maps onto
//      exactly one required scope; a key may hold any subset.
//   2. project scoping — an optional allow-list of project ids. An EMPTY list
//      means "all of the owner's projects"; a non-empty list restricts the key
//      to only those projects (a project-scoped op additionally checks the
//      target projectId ∈ the list).
//
// This module is intentionally free of any AWS SDK / `node:crypto` dependency.
// The actual SHA-256 hashing and random-token generation live in the Node
// services layer; the domain keeps only the PURE vocabulary + authorization
// decision so it can be shared by the authorizer Lambda, the Lambda-backed
// resolvers, and (indirectly) the frontend. Everything here uses plain arrays +
// `indexOf` (no `Set`/`Map`, no `new`, no loops) so it stays safe to bundle into
// the APPSYNC_JS runtime if a resolver ever imports it.
// ===========================================================================
// Scope vocabulary
// ===========================================================================
/**
 * The fixed vocabulary of operation scopes a key may hold. Each value is the
 * scope string persisted on the key and compared against the required scope of
 * a GraphQL field (see {@link FIELD_SCOPES}). The vocabulary covers the full
 * API surface split by resource + read/write, plus the coarse `generation:run`
 * (any generation mutation) and `media:read` (presigned media URLs).
 */
export const ApiKeyScope = {
    projectsRead: 'projects:read',
    projectsWrite: 'projects:write',
    coursesRead: 'courses:read',
    coursesWrite: 'courses:write',
    membersRead: 'members:read',
    membersWrite: 'members:write',
    generationRun: 'generation:run',
    mediaRead: 'media:read',
};
/**
 * Every scope in the vocabulary, as a plain readonly array. Used to validate a
 * requested scope set at key-creation time (reject unknown scopes) and to drive
 * the frontend's scope checkboxes. Kept in sync with {@link ApiKeyScope} by
 * listing each member explicitly (a `Set`-free, loop-free constant).
 */
export const ALL_API_KEY_SCOPES = [
    ApiKeyScope.projectsRead,
    ApiKeyScope.projectsWrite,
    ApiKeyScope.coursesRead,
    ApiKeyScope.coursesWrite,
    ApiKeyScope.membersRead,
    ApiKeyScope.membersWrite,
    ApiKeyScope.generationRun,
    ApiKeyScope.mediaRead,
];
/**
 * Narrow an arbitrary string to a known {@link ApiKeyScope}, or `null` when it
 * is not part of the vocabulary. A linear `indexOf` scan (no `Set`) keeps this
 * APPSYNC_JS-safe; the vocabulary is tiny so the scan is effectively O(1).
 */
export function asApiKeyScope(value) {
    return ALL_API_KEY_SCOPES.indexOf(value) === -1 ? null : value;
}
/**
 * From an arbitrary list of candidate scope strings, keep only the ones in the
 * vocabulary, de-duplicated and in vocabulary order. Used to sanitize a
 * key-creation request so a key never persists an unknown or duplicated scope.
 * Array methods only (no `Set`, no loop) for APPSYNC_JS safety.
 */
export function normalizeScopes(candidates) {
    return ALL_API_KEY_SCOPES.filter((scope) => candidates.indexOf(scope) !== -1);
}
// ===========================================================================
// Field → required-scope map
// ===========================================================================
/**
 * The single required scope for each gated GraphQL field, keyed by field name
 * (the AppSync `info.fieldName`). This is the ONE source of truth the authorizer
 * and the resolver-side enforcement both consult, so an API-key caller is gated
 * identically wherever the check runs.
 *
 * The key-management fields (`myApiKeys`, `createApiKey`, `revokeApiKey`,
 * `deleteApiKey`) are DELIBERATELY ABSENT: they are Cognito-only and can never
 * be authorized for an API-key caller (see {@link KEY_MANAGEMENT_FIELDS} and the
 * authorizer's `deniedFields`). The internal `publishGenerationRequestUpdate`
 * mutation is IAM-only and likewise absent.
 */
export const FIELD_SCOPES = {
    // Queries
    myProjects: ApiKeyScope.projectsRead,
    getProject: ApiKeyScope.projectsRead,
    getCourse: ApiKeyScope.coursesRead,
    getGenerationRequest: ApiKeyScope.coursesRead,
    myGenerationRequests: ApiKeyScope.coursesRead,
    getMediaUrl: ApiKeyScope.mediaRead,
    getMediaUploadUrl: ApiKeyScope.coursesWrite,
    // Mutations
    createProject: ApiKeyScope.projectsWrite,
    createCourse: ApiKeyScope.coursesWrite,
    createVoiceProfile: ApiKeyScope.coursesWrite,
    createArtStyleProfile: ApiKeyScope.coursesWrite,
    inviteUserToProject: ApiKeyScope.membersWrite,
    updateMemberRole: ApiKeyScope.membersWrite,
    removeMember: ApiKeyScope.membersWrite,
    agentGenerateCourse: ApiKeyScope.generationRun,
    approveCourseStructure: ApiKeyScope.generationRun,
    generateEpisodeAudio: ApiKeyScope.generationRun,
    generateEpisodeVideo: ApiKeyScope.generationRun,
};
/**
 * The key-management fields an API-key caller may NEVER invoke: a key cannot
 * mint, list, or revoke keys — only a signed-in Cognito user can. The authorizer
 * returns these as `deniedFields` so AppSync rejects them before a resolver runs
 * even if the key otherwise authorizes the request.
 */
export const KEY_MANAGEMENT_FIELDS = [
    'myApiKeys',
    'createApiKey',
    'revokeApiKey',
    'deleteApiKey',
];
/** Is `fieldName` a key-management field (Cognito-only)? Linear scan; no `Set`. */
export function isKeyManagementField(fieldName) {
    return KEY_MANAGEMENT_FIELDS.indexOf(fieldName) !== -1;
}
/**
 * The required scope for a gated field, or `null` when the field is not gated by
 * a scope (either it is a key-management/internal field, or an unknown field).
 * Callers treat `null` as "no API-key caller may invoke this field".
 */
export function requiredScopeForField(fieldName) {
    const scope = FIELD_SCOPES[fieldName];
    return scope === undefined ? null : scope;
}
/** Does a granted scope set include `scope`? Linear `indexOf` scan (no `Set`). */
export function hasScope(scopes, scope) {
    return scopes.indexOf(scope) !== -1;
}
/**
 * Is `projectId` permitted by a key's project allow-list? An EMPTY allow-list
 * permits every project (the key is not project-scoped); a non-empty list
 * permits only its members. A blank/absent `projectId` on a project-scoped key
 * is denied (a project-scoped operation must name its project).
 */
export function isProjectAllowed(allowedProjectIds, projectId) {
    if (allowedProjectIds.length === 0) {
        return true;
    }
    if (typeof projectId !== 'string' || projectId.length === 0) {
        return false;
    }
    return allowedProjectIds.indexOf(projectId) !== -1;
}
/**
 * The pure API-key authorization decision: may a key with `permissions` invoke
 * `request.fieldName` against `request.projectId`?
 *
 * A field is permitted iff ALL hold:
 *   1. it is NOT a key-management field (those are Cognito-only), and
 *   2. it maps to a known required scope, and
 *   3. the key holds that scope, and
 *   4. the key's project allow-list permits the target `projectId` (an empty
 *      allow-list permits all; a non-project field ignores `projectId`).
 *
 * Unknown fields, key-management fields, and the internal IAM-only mutation all
 * fall through to `false` (deny by default). This function does no I/O.
 */
export function authorizeApiKey(permissions, request) {
    if (isKeyManagementField(request.fieldName)) {
        return false;
    }
    const scope = requiredScopeForField(request.fieldName);
    if (scope === null) {
        return false;
    }
    if (!hasScope(permissions.scopes, scope)) {
        return false;
    }
    // Project scoping only applies to fields that carry a target projectId. A
    // field without a projectId (e.g. `myProjects`, `createProject`) is not
    // project-scoped, so an allow-list cannot exclude it — an empty allow-list
    // permits all, and a non-empty allow-list is simply not consulted here.
    if (isProjectScopedField(request.fieldName)) {
        return isProjectAllowed(permissions.projectIds, request.projectId);
    }
    return true;
}
/**
 * Fields that operate ON a specific project (their arguments carry a
 * `projectId`), so an API-key project allow-list must be enforced against the
 * target project. `myProjects` and `createProject` are NOT project-scoped: the
 * former lists across the owner's projects, the latter creates a new one, so
 * neither names a pre-existing target project. Listing them explicitly keeps
 * the check total and APPSYNC_JS-safe (no `Set`).
 */
const PROJECT_SCOPED_FIELDS = [
    'getProject',
    'getCourse',
    'getGenerationRequest',
    'myGenerationRequests',
    'getMediaUrl',
    'getMediaUploadUrl',
    'createCourse',
    'createVoiceProfile',
    'createArtStyleProfile',
    'inviteUserToProject',
    'updateMemberRole',
    'removeMember',
    'agentGenerateCourse',
    'approveCourseStructure',
    'generateEpisodeAudio',
    'generateEpisodeVideo',
];
/** Does `fieldName` operate on a specific target project? Linear scan; no `Set`. */
export function isProjectScopedField(fieldName) {
    return PROJECT_SCOPED_FIELDS.indexOf(fieldName) !== -1;
}
/**
 * Why a key was rejected at authorization time (or `null`/`ok` when valid). The
 * authorizer maps a non-ok reason to `isAuthorized: false`; the reason is not
 * surfaced to the caller (a rejected key is simply unauthorized) but is useful
 * for structured logs.
 */
export const ApiKeyRejection = {
    revoked: 'revoked',
    expired: 'expired',
};
/**
 * Decide whether a key is currently usable, independent of scopes/project
 * scoping: a revoked key is rejected; an expired key (its `expiresAt` is at or
 * before `nowIso`) is rejected; otherwise it is valid. A key with no `expiresAt`
 * never expires. Comparison is on the ISO 8601 epoch so it is timezone-safe.
 *
 * @param lifecycle the key's `revoked` flag and optional `expiresAt`.
 * @param nowIso    the current instant as an ISO 8601 string (injected).
 */
export function evaluateKeyValidity(lifecycle, nowIso) {
    if (lifecycle.revoked) {
        return { valid: false, reason: ApiKeyRejection.revoked };
    }
    const expiresAt = lifecycle.expiresAt;
    if (typeof expiresAt === 'string' && expiresAt.length > 0) {
        const expiryMs = Date.parse(expiresAt);
        const nowMs = Date.parse(nowIso);
        // A parseable expiry at or before now → expired. An unparseable expiry is
        // treated conservatively as expired (a malformed lifetime cannot extend
        // access).
        if (Number.isNaN(expiryMs)) {
            return { valid: false, reason: ApiKeyRejection.expired };
        }
        if (!Number.isNaN(nowMs) && expiryMs <= nowMs) {
            return { valid: false, reason: ApiKeyRejection.expired };
        }
    }
    return { valid: true };
}
// ===========================================================================
// resolverContext (string) serialization
//
// An AppSync Lambda authorizer returns `resolverContext` whose VALUES MUST be
// strings. Resolvers read them back from `ctx.identity.resolverContext`. These
// helpers are the single encode/decode pair so the authorizer and the resolvers
// agree byte-for-byte on how scopes/projectIds are packed into strings.
// ===========================================================================
/**
 * Join a scope/id list into the comma-separated string carried in
 * `resolverContext`. An empty list encodes as the empty string. No element in
 * the vocabulary or in a project id contains a comma, so a plain `join(',')`
 * round-trips cleanly.
 */
export function joinList(values) {
    return values.join(',');
}
/**
 * Split a comma-separated `resolverContext` value back into its list, dropping
 * empty segments (so `''` → `[]`). The inverse of {@link joinList} for
 * comma-free elements.
 */
export function splitList(value) {
    if (typeof value !== 'string' || value.length === 0) {
        return [];
    }
    return value.split(',').filter((segment) => segment.length > 0);
}
//# sourceMappingURL=api-keys.js.map